import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import SvFactsTab from '../components/SvFactsTab';
import { SvManagePicker } from '../components/SvManage';
import { Topbar, NavTabs, Card, SectionTitle, Loader, Btn, Input, Select, FileUpload } from '../components/UI';
import { supabase } from '../lib/supabase';
import { getActivePeriod, getAllPeriods, importSupervisor, importVacationsFromFile, importServicePct, importDailyVisitFacts, overwriteOrdersFromPlan, importHyperPoints, importOrders, importHyperPlan, importHyperFact, clearVisitFactsForPeriod, getRowsByRouteChunks } from '../lib/data';
import { parseSupervisorFile, parseVacationFile, exportFullProjectToExcel, parseServicePctFile, parseVisitFactsFile, parseHyperFile, parseOrderFile, parseHyperPlanFile, parseHyperFactFile } from '../lib/exportExcel';
import { createUser } from '../lib/supabase';
import RecruitingTab from '../components/RecruitingTab';
import ReferralAdmin from '../components/ReferralAdmin';
import { getGameSettings, saveGameSettings, isValidGameUrl } from '../lib/game';

export default function AdminPage() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState(0);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Topbar
        title="Администратор"
        subtitle={user.full_name}
        rightContent={
          <button onClick={logout} style={{ background: 'rgba(255,255,255,.12)', border: 'none', color: 'white', padding: '6px 12px', borderRadius: 6, fontSize: 12 }}>Выйти</button>
        }
      />
      <NavTabs tabs={['Пользователи', 'Период', 'Импорт', 'Отпуска', 'Service%', 'Сверка', 'Заказ', 'Перезапись', 'Гип.план', 'Гип.факт', 'Факт активностей', 'Выгрузка', 'Игра', 'Подбор', 'Приведи друга']} active={tab} onChange={setTab} />
      <div style={{ padding: 16, maxWidth: tab === 14 ? 980 : 700, margin: '0 auto', width: '100%' }}>
        {tab === 0 && <><UsersTab /><div style={{ maxWidth: 640, margin: '0 auto', padding: '0 16px 24px' }}><SvManagePicker actorId={user.id} /></div></>}
        {tab === 1 && <PeriodTab />}
        {tab === 2 && <ImportTab />}
        {tab === 3 && <VacationImportTab userId={user.id} />}
        {tab === 4 && <ServicePctTab />}
        {tab === 5 && <VisitFactsTab />}
        {tab === 6 && <OrderImportTab />}
        {tab === 7 && <OrderOverwriteTab />}
        {tab === 8 && <HyperPlanTab />}
        {tab === 9 && <HyperFactTab />}
        {tab === 10 && <SvFactsTab actorId={user.id} />}
        {tab === 11 && <ExportTab />}
        {tab === 12 && <GameTab />}
        {tab === 13 && <RecruitingTab actorId={user.id} />}
        {tab === 14 && <ReferralAdmin />}
      </div>
    </div>
  );
}

// ── ПОЛЬЗОВАТЕЛИ ─────────────────────────────────────────────────────────────
function UsersTab() {
  const [users, setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm]       = useState({ login: '', full_name: '', role: 'sv', tm_id: '' });
  const [tms, setTms]         = useState([]);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState('');

  useEffect(() => { loadUsers(); }, []);

  async function loadUsers() {
    setLoading(true);
    const { data } = await supabase.from('users').select('*').eq('is_active', true).order('role').order('full_name');
    setUsers(data || []);
    setTms((data || []).filter(u => u.role === 'tm'));
    setLoading(false);
  }

  async function handleAdd() {
    if (!form.login || !form.full_name) { setMsg('Заполните все поля'); return; }
    setSaving(true); setMsg('');
    try {
      await createUser({ ...form, tm_id: form.tm_id || null });
      setShowAdd(false);
      setForm({ login: '', full_name: '', role: 'sv', tm_id: '' });
      setMsg('✓ Пользователь создан');
      loadUsers();
    } catch (err) { setMsg('Ошибка: ' + err.message); }
    finally { setSaving(false); }
  }

  async function deactivateUser(id) {
    await supabase.from('users').update({ is_active: false }).eq('id', id);
    loadUsers();
  }

  async function handleExportUsers() {
    const { getAllActiveUsersForExport } = await import('../lib/data');
    const { exportUsersToExcel } = await import('../lib/exportExcel');
    const list = await getAllActiveUsersForExport();
    exportUsersToExcel(list, 'Учётки_' + new Date().toISOString().split('T')[0]);
  }

  const roleLabel = { sv: 'СВ', tm: 'ТМ', rm: 'РМ', trainer: 'Тренер', admin: 'Admin' };

  if (loading) return <Loader />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <SectionTitle>Пользователи ({users.length})</SectionTitle>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn size="sm" color="ghost" onClick={handleExportUsers}>📥 Выгрузить</Btn>
          <Btn size="sm" onClick={() => setShowAdd(!showAdd)}>+ Добавить вручную</Btn>
        </div>
      </div>

      <div style={{ background: 'var(--accent-light)', border: '1px solid #BDD7EE', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: 'var(--accent-dark)' }}>
        ℹ️ Аккаунты СВ и ТМ создаются автоматически при импорте SUPERVISOR. Здесь можно добавить РМ или недостающего вручную.
      </div>

      {showAdd && (
        <Card style={{ marginBottom: 12 }}>
          <div style={{ padding: 16 }}>
            <Input label="ФИО" value={form.full_name} onChange={v => setForm(f => ({...f, full_name: v}))} placeholder="Иванова Мария Петровна" />
            <Input label="Код для входа" value={form.login} onChange={v => setForm(f => ({...f, login: v}))} placeholder="sv_1054-SV061 или bereza" />
            <Select label="Роль" value={form.role} onChange={v => setForm(f => ({...f, role: v}))} options={[
              { value: 'sv', label: 'Супервайзер (СВ)' },
              { value: 'tm', label: 'Территориальный менеджер (ТМ)' },
              { value: 'rm', label: 'Региональный менеджер (РМ)' },
              { value: 'trainer', label: 'Тренер (только активности СВ)' },
              { value: 'admin', label: 'Администратор' },
            ]} />
            {form.role === 'sv' && tms.length > 0 && (
              <Select label="ТМ (для СВ)" value={form.tm_id} onChange={v => setForm(f => ({...f, tm_id: v}))}
                options={[{ value: '', label: '— Не привязан —' }, ...tms.map(t => ({ value: t.id, label: t.full_name }))]}
              />
            )}
            {msg && <div style={{ fontSize: 12, color: msg.startsWith('✓') ? 'var(--green)' : 'var(--red)', marginBottom: 10 }}>{msg}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn color="ghost" size="md" onClick={() => setShowAdd(false)} style={{ flex: 1 }}>Отмена</Btn>
              <Btn color="primary" size="md" onClick={handleAdd} disabled={saving} style={{ flex: 1 }}>
                {saving ? 'Создание...' : 'Создать'}
              </Btn>
            </div>
          </div>
        </Card>
      )}

      {msg && !showAdd && <div style={{ fontSize: 12, color: 'var(--green)', marginBottom: 10 }}>{msg}</div>}

      <Card>
        {users.map(u => (
          <div key={u.id} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{u.full_name}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                Код: <b>{u.login}</b> · <span style={{ background: 'var(--accent-light)', color: 'var(--accent)', padding: '1px 6px', borderRadius: 4, fontWeight: 600 }}>{roleLabel[u.role]}</span>
              </div>
            </div>
            <Btn size="sm" color="ghost" onClick={() => deactivateUser(u.id)}>Деактивировать</Btn>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ── ПЕРИОД ───────────────────────────────────────────────────────────────────
function PeriodTab() {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ year: 2026, month: 10 });
  const [msg, setMsg] = useState('');

  useEffect(() => { loadPeriods(); }, []);

  async function loadPeriods() {
    const p = await getAllPeriods();
    setPeriods(p || []);
    setLoading(false);
  }

  function getISOWeek(dt) {
    const d = new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  }

  async function createPeriod() {
    setCreating(true); setMsg('');
    try {
      const { year, month } = form;
      const daysInMonth = new Date(year, month, 0).getDate();
      const monthNames = ['','Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];

      const { data: p } = await supabase.from('work_periods').insert({
        year, month, name: `${monthNames[month]} ${year}`,
        date_from: `${year}-${String(month).padStart(2,'0')}-01`,
        date_to:   `${year}-${String(month).padStart(2,'0')}-${daysInMonth}`,
        is_active: false,
      }).select().single();

      const weeks = {};
      for (let d = 1; d <= daysInMonth; d++) {
        const dt = new Date(year, month-1, d);
        const w = getISOWeek(dt);
        if (!weeks[w]) weeks[w] = { from: d, to: d };
        else weeks[w].to = d;
      }

      let sort = 1;
      for (const [w, range] of Object.entries(weeks)) {
        await supabase.from('work_weeks').insert({
          period_id:   p.id,
          week_number: parseInt(w),
          date_from:   `${year}-${String(month).padStart(2,'0')}-${String(range.from).padStart(2,'0')}`,
          date_to:     `${year}-${String(month).padStart(2,'0')}-${String(range.to).padStart(2,'0')}`,
          sort_order:  sort++,
        });
      }

      setMsg(`✓ Период "${monthNames[month]} ${year}" создан (${Object.keys(weeks).length} недель)`);
      loadPeriods();
    } catch (err) {
      setMsg('Ошибка: ' + err.message);
    } finally {
      setCreating(false);
    }
  }

  async function setActive(periodId) {
    await supabase.from('work_periods').update({ is_active: false }).neq('id', periodId);
    await supabase.from('work_periods').update({ is_active: true }).eq('id', periodId);
    loadPeriods();
  }

  if (loading) return <Loader />;

  const MONTHS = [
    { value: 1, label: 'Январь' }, { value: 2, label: 'Февраль' }, { value: 3, label: 'Март' },
    { value: 4, label: 'Апрель' }, { value: 5, label: 'Май' },     { value: 6, label: 'Июнь' },
    { value: 7, label: 'Июль' },  { value: 8, label: 'Август' },   { value: 9, label: 'Сентябрь' },
    { value: 10, label: 'Октябрь' }, { value: 11, label: 'Ноябрь' }, { value: 12, label: 'Декабрь' },
  ];

  return (
    <div>
      <SectionTitle>Создать новый период</SectionTitle>
      <Card style={{ marginBottom: 12 }}>
        <div style={{ padding: 16 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <Select label="Месяц" value={form.month} onChange={v => setForm(f => ({...f, month: parseInt(v)}))}
              options={MONTHS} style={{ flex: 2 }} />
            <Input label="Год" type="number" value={form.year} onChange={v => setForm(f => ({...f, year: parseInt(v)}))}
              style={{ flex: 1 }} />
          </div>
          {msg && <div style={{ fontSize: 12, color: msg.startsWith('✓') ? 'var(--green)' : 'var(--red)', marginBottom: 10 }}>{msg}</div>}
          <Btn size="full" onClick={createPeriod} disabled={creating}>
            {creating ? 'Создание...' : '+ Создать период'}
          </Btn>
        </div>
      </Card>

      <SectionTitle>Все периоды</SectionTitle>
      <Card>
        {periods.map(p => (
          <div key={p.id} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.date_from} — {p.date_to}</div>
            </div>
            {p.is_active
              ? <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--green)', background: 'var(--green-bg)', padding: '3px 10px', borderRadius: 20 }}>● Активный</span>
              : <Btn size="sm" color="ghost" onClick={() => setActive(p.id)}>Сделать активным</Btn>
            }
          </div>
        ))}
      </Card>

    </div>
  );
}

// ── ИМПОРТ SUPERVISOR ─────────────────────────────────────────────────────────
function ImportTab() {
  const [file, setFile]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [report, setReport]   = useState(null);
  const [error, setError]     = useState('');

  async function handleImport() {
    if (!file) return;
    setLoading(true); setError(''); setReport(null);
    try {
      const rows = await parseSupervisorFile(file);
      const rep = await importSupervisor(rows);
      setReport(rep);
      setFile(null);
    } catch (err) {
      setError('Ошибка: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <SectionTitle>Импорт файла SUPERVISOR</SectionTitle>
      <div style={{ background: 'var(--accent-light)', border: '1px solid #BDD7EE', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: 'var(--accent-dark)' }}>
        📋 Загрузите файл SUPERVISOR_Месяц.xlsx — система обновит маршруты, СВ и ТМ, автоматически создаст недостающие аккаунты
      </div>

      <Card>
        <div style={{ padding: 16 }}>
          <FileUpload label="Файл SUPERVISOR (.xlsx)" accept=".xlsx,.xls" onFile={setFile} hint="SUPERVISOR_Сентябрь.xlsx" />
          {file && <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>📄 {file.name}</div>}
          {error && <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 10 }}>{error}</div>}
          <Btn size="full" onClick={handleImport} disabled={!file || loading}>
            {loading ? 'Загрузка...' : 'Загрузить'}
          </Btn>
        </div>
      </Card>

      {report && (
        <>
          <SectionTitle>Результат импорта</SectionTitle>
          <Card>
            <div style={{ padding: 16 }}>
              <div style={{ fontSize: 13, marginBottom: 8 }}>✓ Обновлено маршрутов: <b>{report.routesUpdated}</b></div>

              {report.svAccountsCreated.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--green)', marginBottom: 6 }}>
                    Созданы новые аккаунты СВ ({report.svAccountsCreated.length}):
                  </div>
                  {report.svAccountsCreated.map((a, i) => (
                    <div key={i} style={{ fontSize: 12, padding: '4px 0', color: 'var(--text-muted)' }}>
                      <b>{a.login}</b> — {a.full_name}
                    </div>
                  ))}
                </div>
              )}

              {report.tmAccountsCreated.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--green)', marginBottom: 6 }}>
                    Созданы новые аккаунты ТМ ({report.tmAccountsCreated.length}):
                  </div>
                  {report.tmAccountsCreated.map((a, i) => (
                    <div key={i} style={{ fontSize: 12, padding: '4px 0', color: 'var(--text-muted)' }}>
                      <b>{a.login}</b> — {a.full_name}
                    </div>
                  ))}
                </div>
              )}

              {report.vacancyClosed.length > 0 && (
                <div style={{ marginTop: 12, background: '#FFFDE7', border: '1px solid #F9A825', borderRadius: 6, padding: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#7D5A00', marginBottom: 6 }}>
                    ⭐ Закрыты вакансии ({report.vacancyClosed.length}):
                  </div>
                  {report.vacancyClosed.map((v, i) => (
                    <div key={i} style={{ fontSize: 12, padding: '4px 0' }}>
                      {v.old_name} → <b>{v.new_name}</b>
                    </div>
                  ))}
                </div>
              )}

              {report.mrcChanged.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6 }}>
                    Смена МРЧ на маршруте ({report.mrcChanged.length}):
                  </div>
                  {report.mrcChanged.map((c, i) => (
                    <div key={i} style={{ fontSize: 12, padding: '4px 0', color: 'var(--text-muted)' }}>
                      {c.route_internal_code}: {c.old_name} → {c.new_name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

// ── ИМПОРТ ОТПУСКОВ ───────────────────────────────────────────────────────────
function VacationImportTab({ userId }) {
  const [file, setFile]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult]   = useState(null);
  const [error, setError]     = useState('');
  const [period, setPeriod]   = useState(null);

  useEffect(() => { getActivePeriod().then(setPeriod).catch(console.error); }, []);

  async function handleImport() {
    if (!file || !period) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const rows = await parseVacationFile(file, period.year, period.month);
      const res = await importVacationsFromFile(period.id, rows, userId);
      setResult(res);
      setFile(null);
    } catch (err) {
      setError('Ошибка: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <SectionTitle>Импорт графика отпусков</SectionTitle>
      <div style={{ background: '#FFFDE7', border: '1px solid #F9A825', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#7D5A00' }}>
        📅 Загрузите файл графика отпусков (CSV UTF-8 или Excel). Система автоматически найдёт отпуска за активный месяц ({period?.name || '...'})
      </div>
      <Card>
        <div style={{ padding: 16 }}>
          <FileUpload label="График отпусков (.csv или .xlsx)" accept=".csv,.xlsx,.xls" onFile={setFile} hint="График_отпусков_2026_Мрч.csv" />
          {file && <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>📄 {file.name}</div>}
          {error && <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 10 }}>{error}</div>}
          {result && (
            <div style={{ fontSize: 12, color: 'var(--green)', marginBottom: 10 }}>
              ✓ Загружено {result.imported} из {result.total} отпусков
            </div>
          )}
          <Btn size="full" onClick={handleImport} disabled={!file || loading || !period}>
            {loading ? 'Загрузка...' : 'Загрузить'}
          </Btn>
        </div>
      </Card>
    </div>
  );
}

// ── ПОЛНАЯ ВЫГРУЗКА ───────────────────────────────────────────────────────────
function ExportTab() {
  const [period, setPeriod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { getActivePeriod().then(setPeriod).catch(console.error); }, []);

  async function handleExport() {
    if (!period) return;
    setLoading(true); setError('');
    try {
      // Собираем все ТМ, их СВ, маршруты и статусы
      const { data: tms } = await supabase.from('users').select('*').eq('role', 'tm').eq('is_active', true).order('full_name');

      const tmData = await Promise.all((tms || []).map(async tm => {
        const { data: svList } = await supabase.from('supervisors').select('*').eq('tm_id', tm.id).eq('is_active', true);
        const svIds = (svList || []).map(s => s.id);

        let routes = [];
        if (svIds.length) {
          const { data: r } = await supabase.from('routes').select('*, merchandisers(*), supervisors!sv_id(full_name, internal_code, external_code)').in('sv_id', svIds).eq('is_active', true);
          routes = r || [];
        }

        const routeIds = routes.map(r => r.id);
        let statusMap = {};
        if (routeIds.length) {
          const statusList = await getRowsByRouteChunks('daily_statuses', 'route_id, work_date, status_code', period.id, routeIds);
          (statusList || []).forEach(s => {
            if (!statusMap[s.route_id]) statusMap[s.route_id] = {};
            statusMap[s.route_id][s.work_date] = s.status_code;
          });
        }

        return { tm, routes, statuses: statusMap };
      }));

      exportFullProjectToExcel({ tmData, year: period.year, month: period.month, fileName: `Статус_выхода_MR_${period.name.replace(' ', '_')}` });
    } catch (err) {
      setError('Ошибка: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <SectionTitle>Полная выгрузка в Excel</SectionTitle>
      <div style={{ background: 'var(--accent-light)', border: '1px solid #BDD7EE', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: 'var(--accent-dark)' }}>
        📥 Выгрузит один файл со всеми листами: по листу на каждого ТМ + сводные таблицы
      </div>
      <Card>
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 13, marginBottom: 12 }}>Активный период: <b>{period?.name || '...'}</b></div>
          {error && <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 10 }}>{error}</div>}
          <Btn size="full" onClick={handleExport} disabled={!period || loading}>
            {loading ? 'Формирование файла...' : '📥 Скачать полную выгрузку'}
          </Btn>
        </div>
      </Card>
    </div>
  );
}

// ── SERVICE% ЗА НЕДЕЛЮ ────────────────────────────────────────────────────────
function ServicePctTab() {
  const [file, setFile]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult]   = useState(null);
  const [error, setError]     = useState('');
  const [period, setPeriod]   = useState(null);

  useEffect(() => { getActivePeriod().then(setPeriod).catch(console.error); }, []);

  async function handleImport() {
    if (!file || !period) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const rows = await parseServicePctFile(file);
      const count = await importServicePct(period.id, rows);
      setResult(count);
      setFile(null);
    } catch (err) {
      setError('Ошибка: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <SectionTitle>Service% — загрузка за неделю</SectionTitle>
      <div style={{ background: 'var(--accent-light)', border: '1px solid #BDD7EE', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: 'var(--accent-dark)' }}>
        📊 Загружайте раз в неделю — файл с колонками TM Name, SV Name, ROUTE_NAME, ISO Weeknumber, Visits Plan, Visits Fact, Visits Service%
      </div>
      <Card>
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 13, marginBottom: 12 }}>Активный период: <b>{period?.name || '...'}</b></div>
          <FileUpload label="Файл Service% (.xlsx)" accept=".xlsx,.xls" onFile={setFile} hint="data_service.xlsx" />
          {file && <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>📄 {file.name}</div>}
          {error && <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 10 }}>{error}</div>}
          {result != null && (
            <div style={{ fontSize: 12, color: 'var(--green)', marginBottom: 10 }}>
              ✓ Загружено {result} записей Service%
            </div>
          )}
          <Btn size="full" onClick={handleImport} disabled={!file || loading || !period}>
            {loading ? 'Загрузка...' : 'Загрузить'}
          </Btn>
        </div>
      </Card>
    </div>
  );
}

// ── ГИППЕРА · ПЛАН(ЗАКАЗ): КАЛЕНДАРЬ ВИЗИТОВ ──────────────────────────────────
function HyperPlanTab() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState(null);
  useEffect(() => { getActivePeriod().then(setPeriod).catch(console.error); }, []);

  async function handleImport() {
    if (!file || !period) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const parsed = await parseHyperPlanFile(file);
      const res = await importHyperPlan(period.id, parsed);
      setResult(res); setFile(null);
    } catch (err) { setError('Ошибка: ' + err.message); }
    finally { setLoading(false); }
  }

  return (
    <div>
      <SectionTitle>Гиппера · план (заказ) на месяц</SectionTitle>
      <div style={{ background: 'var(--accent-light)', border: '1px solid #BDD7EE', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: 'var(--accent-dark)' }}>
        📦 Файл-календарь: колонки «Внутренний код маршрута», «Внешний код клиента» (ship_to), клиент/адрес/гео, далее колонка на каждый день (1 = визит запланирован). Заводит точки-гипперы и план(заказ).
      </div>
      <Card>
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 13, marginBottom: 12 }}>Активный период: <b>{period?.name || '...'}</b></div>
          <FileUpload label="Файл плана гипперов (.xlsx)" accept=".xlsx,.xls" onFile={setFile} hint="гиппера_план.xlsx" />
          {file && <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>📄 {file.name}</div>}
          {error && <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 10 }}>{error}</div>}
          {result && (
            <div style={{ fontSize: 12, color: 'var(--green)', marginBottom: 10 }}>
              ✓ Точек: {result.points}, дней плана: {result.planRows}.
              {result.unmatchedRoutes > 0 && <div style={{ color: 'var(--red)', marginTop: 4 }}>Не сопоставлено с маршрутами: {result.unmatchedRoutes}{result.unmatchedSample?.length ? ` (напр. ${result.unmatchedSample.join(', ')})` : ''}</div>}
            </div>
          )}
          <Btn size="full" onClick={handleImport} disabled={!file || loading || !period}>{loading ? 'Загрузка...' : 'Загрузить план гипперов'}</Btn>
        </div>
      </Card>
    </div>
  );
}

// ── ГИППЕРА · ФАКТ (из BI) ────────────────────────────────────────────────────
function HyperFactTab() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState(null);
  useEffect(() => { getActivePeriod().then(setPeriod).catch(console.error); }, []);

  async function handleImport() {
    if (!file || !period) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const rows = await parseHyperFactFile(file);
      const res = await importHyperFact(period.id, rows);
      setResult(res); setFile(null);
    } catch (err) { setError('Ошибка: ' + err.message); }
    finally { setLoading(false); }
  }

  return (
    <div>
      <SectionTitle>Гиппера · факт</SectionTitle>
      <div style={{ background: 'var(--accent-light)', border: '1px solid #BDD7EE', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: 'var(--accent-dark)' }}>
        📊 BI-файл: строка заголовков содержит SHIP_TO, SV Name, Date, Visits Fact. Факт матчится к точке по SHIP_TO (+ SV для развязки).
      </div>
      <Card>
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 13, marginBottom: 12 }}>Активный период: <b>{period?.name || '...'}</b></div>
          <FileUpload label="Файл факта гипперов (.xlsx)" accept=".xlsx,.xls" onFile={setFile} hint="data_XX.xlsx" />
          {file && <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>📄 {file.name}</div>}
          {error && <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 10 }}>{error}</div>}
          {result && (
            <div style={{ fontSize: 12, color: 'var(--green)', marginBottom: 10 }}>
              ✓ Загружено {result.imported} записей факта.
              {result.skipped > 0 && <div style={{ color: 'var(--red)', marginTop: 4 }}>Не сопоставлено (нет такой точки): {result.skipped}</div>}
            </div>
          )}
          <Btn size="full" onClick={handleImport} disabled={!file || loading || !period}>{loading ? 'Загрузка...' : 'Загрузить факт гипперов'}</Btn>
        </div>
      </Card>
    </div>
  );
}

// ── ЗАКАЗ: ЗАГРУЗКА ФАЙЛА ЗАКАЗА НА МЕСЯЦ ─────────────────────────────────────
function OrderImportTab() {
  const [file, setFile]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult]   = useState(null);
  const [error, setError]     = useState('');
  const [period, setPeriod]   = useState(null);

  useEffect(() => { getActivePeriod().then(setPeriod).catch(console.error); }, []);

  async function handleImport() {
    if (!file || !period) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const rows = await parseOrderFile(file);
      const res = await importOrders(period.id, rows);
      setResult(res);
      setFile(null);
    } catch (err) {
      setError('Ошибка: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <SectionTitle>Заказ — загрузка на месяц</SectionTitle>
      <div style={{ background: 'var(--accent-light)', border: '1px solid #BDD7EE', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: 'var(--accent-dark)' }}>
        📦 Широкий файл: колонка «Маршрут» (код 1054-00001), «Значения» (Кол-во тт), далее колонка на каждый день месяца. Значение = заказ визитов на день. Можно перегружать при обновлении заказа — заказ (эталон из файла) перезапишется.
      </div>
      <Card>
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 13, marginBottom: 12 }}>Активный период: <b>{period?.name || '...'}</b></div>
          <FileUpload label="Файл заказа (.xlsx)" accept=".xlsx,.xls" onFile={setFile} hint="Заказ на все маршруты.xlsx" />
          {file && <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>📄 {file.name}</div>}
          {error && <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 10 }}>{error}</div>}
          {result && (
            <div style={{ fontSize: 12, color: 'var(--green)', marginBottom: 10 }}>
              ✓ Загружено {result.imported} записей заказа по {result.routesMatched} маршрутам.
              {result.skipped > 0 && (
                <div style={{ color: 'var(--red)', marginTop: 4 }}>
                  Не сопоставлено с маршрутами: {result.skipped}
                  {result.unmatchedSample?.length ? ` (напр. ${result.unmatchedSample.join(', ')})` : ''}
                </div>
              )}
            </div>
          )}
          <Btn size="full" onClick={handleImport} disabled={!file || loading || !period}>
            {loading ? 'Загрузка...' : 'Загрузить заказ'}
          </Btn>
        </div>
      </Card>
    </div>
  );
}

// ── СВЕРКА: ЗАГРУЗКА ФАКТОВ ВИЗИТОВ ЗА ДЕНЬ ──────────────────────────────────
function VisitFactsTab() {
  const [file, setFile]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult]   = useState(null);
  const [error, setError]     = useState('');
  const [period, setPeriod]   = useState(null);

  useEffect(() => { getActivePeriod().then(setPeriod).catch(console.error); }, []);

  async function handleImport() {
    if (!file || !period) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const rows = await parseVisitFactsFile(file);
      const res = await importDailyVisitFacts(period.id, rows);
      setResult(res);
      setFile(null);
    } catch (err) {
      setError('Ошибка: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleClear() {
    if (!period) return;
    if (!window.confirm(`Удалить ВЕСЬ факт визитов за период «${period.name}»?\n\nЭто нужно для чистой перезаливки после исправления дат. После очистки заново загрузите все файлы факта за месяц.`)) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const n = await clearVisitFactsForPeriod(period.id);
      setError(''); setResult({ cleared: n });
    } catch (err) {
      setError('Ошибка очистки: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <SectionTitle>Сверка — факт визитов за день</SectionTitle>
      <div style={{ background: 'var(--accent-light)', border: '1px solid #BDD7EE', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: 'var(--accent-dark)' }}>
        📊 Загружайте каждый день — файл с колонками TM Name, SV Name, ROUTE_NAME, Date, Visits Plan, Visits Fact, Visits Service%. Система сравнит факт визитов со статусами, которые проставили СВ, и покажет расхождения на вкладке "Сверка" у СВ/ТМ/РМ. Исходный файл не сохраняется — только распознанные данные.
      </div>
      <Card>
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 13, marginBottom: 12 }}>Активный период: <b>{period?.name || '...'}</b></div>
          <FileUpload label="Файл факта визитов (.xlsx)" accept=".xlsx,.xls" onFile={setFile} hint="data_15.xlsx" />
          {file && <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>📄 {file.name}</div>}
          {error && <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 10 }}>{error}</div>}
          {result && result.cleared != null && (
            <div style={{ fontSize: 12, marginBottom: 10, border: '1px solid #E2E8F0', borderRadius: 8, padding: '10px 12px', background: '#F8FAFC', color: 'var(--green)', fontWeight: 600 }}>
              ✓ Удалено записей факта: {result.cleared}. Теперь заново загрузите файлы факта за месяц.
            </div>
          )}
          {result && result.cleared == null && (
            <div style={{ fontSize: 12, marginBottom: 10, border: '1px solid #E2E8F0', borderRadius: 8, padding: '10px 12px', background: '#F8FAFC' }}>
              <div style={{ color: 'var(--green)', fontWeight: 600, marginBottom: 6 }}>✓ Импорт завершён</div>
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <tbody>
                  <tr><td style={{ padding: '2px 0', color: 'var(--text-muted)' }}>Строк в файле</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{result.total}</td></tr>
                  <tr><td style={{ padding: '2px 0', color: 'var(--text-muted)' }}>Сопоставлено с маршрутами</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{result.matchedRows}</td></tr>
                  <tr><td style={{ padding: '2px 0', color: result.skipped ? 'var(--red)' : 'inherit' }}>Не сопоставлено (кодов маршрутов)</td><td style={{ textAlign: 'right', fontWeight: 600, color: result.skipped ? 'var(--red)' : 'inherit' }}>{result.skipped} строк · {result.unmatchedCount} код.</td></tr>
                  <tr><td colSpan={2} style={{ borderTop: '1px solid #E2E8F0', paddingTop: 4 }}></td></tr>
                  <tr><td style={{ padding: '2px 0', color: 'var(--text-muted)' }}>Визитов (Visits Fact) в файле</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{result.factTotal}</td></tr>
                  {result.mergedDuplicates > 0 && (
                    <tr><td style={{ padding: '2px 0', color: 'var(--text-muted)' }}>Объединено дублей (маршрут+дата)</td><td style={{ textAlign: 'right' }}>{result.mergedDuplicates} строк</td></tr>
                  )}
                  <tr><td style={{ padding: '2px 0', color: 'var(--text-muted)' }}>Записано в базу (визитов)</td><td style={{ textAlign: 'right', fontWeight: 600, color: result.storedFact === result.factMatched ? 'var(--green)' : 'var(--red)' }}>{result.storedFact}</td></tr>
                  {result.factLost > 0 && (
                    <tr><td style={{ padding: '2px 0', color: 'var(--red)' }}>Потеряно (несопоставленные коды)</td><td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--red)' }}>{result.factLost}</td></tr>
                  )}
                  {result.dateFrom && (
                    <tr><td style={{ padding: '2px 0', color: 'var(--text-muted)' }}>Диапазон дат в файле</td><td style={{ textAlign: 'right' }}>{result.dateFrom} … {result.dateTo}</td></tr>
                  )}
                </tbody>
              </table>
              {result.unmatchedSample && result.unmatchedSample.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
                  Примеры несопоставленных кодов: {result.unmatchedSample.filter(Boolean).join(', ')}{result.unmatchedCount > result.unmatchedSample.length ? ' …' : ''}
                  <div style={{ marginTop: 4 }}>Такие коды <b>ROUTE_NAME</b> отсутствуют в <b>routes.external_code</b> — проверьте, что импорт SUPERVISOR завёл эти маршруты.</div>
                </div>
              )}
            </div>
          )}
          <Btn size="full" onClick={handleImport} disabled={!file || loading || !period}>
            {loading ? 'Загрузка...' : 'Загрузить'}
          </Btn>
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #E2E8F0' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
              ⚠️ Если раньше факт грузился со сдвинутыми датами — очистите факт за период и залейте все файлы заново.
            </div>
            <button
              onClick={handleClear}
              disabled={loading || !period}
              style={{ width: '100%', padding: '9px 12px', fontSize: 12, fontWeight: 600, color: 'var(--red)', background: '#fff', border: '1px solid var(--red)', borderRadius: 8, cursor: loading || !period ? 'default' : 'pointer', opacity: loading || !period ? .5 : 1 }}
            >
              Очистить факт за период
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ── ПЕРЕЗАПИСЬ ЗАКАЗА ПО ВЫБРАННОМУ ДИАПАЗОНУ ДАТ ────────────────────────────
function OrderOverwriteTab() {
  const [file, setFile]       = useState(null);
  const [from, setFrom]       = useState('');
  const [to, setTo]           = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult]   = useState(null);
  const [error, setError]     = useState('');
  const [period, setPeriod]   = useState(null);

  useEffect(() => { getActivePeriod().then(setPeriod).catch(console.error); }, []);

  async function handleOverwrite() {
    if (!file || !period || !from || !to) return;
    if (from > to) { setError('Дата «с» не может быть позже «по».'); return; }
    if (!window.confirm(`Перезаписать ЗАКАЗ за ${from} … ${to} из этого файла?\n\nПо ВСЕМ активным маршрутам в этом диапазоне заказ станет = Visits Plan из файла, а дни/маршруты, которых в файле нет (включая маршруты, отсутствующие в файле целиком), станут 0. Прежние значения сохранятся (мелко сверху в табеле).`)) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const rows = await parseVisitFactsFile(file);
      const res = await overwriteOrdersFromPlan(period.id, rows, from, to);
      setResult(res);
      setFile(null);
    } catch (err) {
      setError('Ошибка: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <SectionTitle>Перезапись заказа по датам</SectionTitle>
      <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#92400E' }}>
        ⚠️ Жёсткая перезапись. Выбери диапазон дат и загрузи план-факт файл (те же колонки, что в «Сверке»: ROUTE_NAME, Date, Visits Plan…). За эти даты по маршрутам из файла заказ станет равен <b>Visits Plan</b>. Если маршрута/дня в файле нет — заказ за этот день станет <b>0</b>. Так корректно отрабатываются переносы визитов. Прежнее значение сохраняется и покажется мелко сверху.
      </div>
      <Card>
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 13, marginBottom: 12 }}>Активный период: <b>{period?.name || '...'}</b></div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Дата с<br/>
              <input type="date" value={from} onChange={e => setFrom(e.target.value)}
                style={{ marginTop: 4, padding: '7px 10px', fontSize: 13, border: '1px solid #CBD5E1', borderRadius: 7 }} />
            </label>
            <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Дата по<br/>
              <input type="date" value={to} onChange={e => setTo(e.target.value)}
                style={{ marginTop: 4, padding: '7px 10px', fontSize: 13, border: '1px solid #CBD5E1', borderRadius: 7 }} />
            </label>
          </div>
          <FileUpload label="План-факт файл (.xlsx)" accept=".xlsx,.xls" onFile={setFile} hint="data_XX.xlsx" />
          {file && <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>📄 {file.name}</div>}
          {error && <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 10 }}>{error}</div>}
          {result && (
            <div style={{ fontSize: 12, marginBottom: 10, border: '1px solid #E2E8F0', borderRadius: 8, padding: '10px 12px', background: '#F8FAFC' }}>
              <div style={{ color: 'var(--green)', fontWeight: 600, marginBottom: 6 }}>✓ Перезапись завершена ({result.from} … {result.to})</div>
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <tbody>
                  <tr><td style={{ padding: '2px 0', color: 'var(--text-muted)' }}>Маршрутов из файла</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{result.routesInFile ?? result.routes}</td></tr>
                  <tr><td style={{ padding: '2px 0', color: 'var(--text-muted)' }}>Обработано активных маршрутов всего</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{result.routes}</td></tr>
                  <tr><td style={{ padding: '2px 0', color: 'var(--text-muted)' }}>Заказов проставлено (&gt;0)</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{result.overwritten}</td></tr>
                  <tr><td style={{ padding: '2px 0', color: 'var(--text-muted)' }}>Обнулено (визит перенесён/не было/не было в файле)</td><td style={{ textAlign: 'right', fontWeight: 600, color: result.zeroed ? '#B45309' : 'inherit' }}>{result.zeroed}</td></tr>
                </tbody>
              </table>
              {result.unmatched && result.unmatched.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
                  Несопоставленные коды: {result.unmatched.filter(Boolean).join(', ')} … (нет в routes.external_code)
                </div>
              )}
            </div>
          )}
          <Btn size="full" onClick={handleOverwrite} disabled={!file || !from || !to || loading || !period}>
            {loading ? 'Перезапись...' : 'Перезаписать заказ за диапазон'}
          </Btn>
        </div>
      </Card>
    </div>
  );
}

// ── ИМПОРТ "ГИППЕРА" (точки внутри маршрута) ─────────────────────────────────
function HyperImportTab() {
  const [file, setFile]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult]   = useState(null);
  const [error, setError]     = useState('');

  async function handleImport() {
    if (!file) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const rows = await parseHyperFile(file);
      const res = await importHyperPoints(rows);
      setResult(res);
      setFile(null);
    } catch (err) {
      setError('Ошибка: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <SectionTitle>Импорт точек "Гиппера"</SectionTitle>
      <div style={{ background: 'var(--accent-light)', border: '1px solid #BDD7EE', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: 'var(--accent-dark)' }}>
        🏪 Загружайте раз в месяц (или по обновлению) — файл с колонками TM Name, SV Name, ROUTE_NAME, SHIP_TO, CLIENT, Адрес, ПРИОРИТЕТ МАРШРУТА, Сцеп. Точки автоматически привязываются к маршрутам и появляются на вкладке "Планирование" у СВ.
      </div>
      <Card>
        <div style={{ padding: 16 }}>
          <FileUpload label="Файл точек Гиппера (.xlsx)" accept=".xlsx,.xls" onFile={setFile} hint="ЛМ_гиппера.xlsx" />
          {file && <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>📄 {file.name}</div>}
          {error && <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 10 }}>{error}</div>}
          {result && (
            <div style={{ fontSize: 12, color: 'var(--green)', marginBottom: 10 }}>
              ✓ Загружено {result.imported} точек (пропущено без совпадения маршрута: {result.skipped})
            </div>
          )}
          <Btn size="full" onClick={handleImport} disabled={!file || loading}>
            {loading ? 'Загрузка...' : 'Загрузить'}
          </Btn>
        </div>
      </Card>
    </div>
  );
}

// ── ИГРА «ЛОВИМ ДОЛЮ» ────────────────────────────────────────────────────────
// Кнопка «Сыграть в Игру» у СВ: показывать или нет + ссылка на игру (отдельный хостинг).
function GameTab() {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [url, setUrl] = useState('');
  const [loadErr, setLoadErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    getGameSettings().then(s => {
      setEnabled(!!s.enabled); setUrl(s.url || ''); setLoadErr(s.error || ''); setLoading(false);
    });
  }, []);

  const urlOk = isValidGameUrl(url);
  async function save() {
    setSaving(true); setMsg('');
    try {
      await saveGameSettings({ enabled, url });
      setLoadErr('');
      setMsg(enabled && urlOk ? '✓ Сохранено. Кнопка появится у СВ при следующем открытии приложения.' : '✓ Сохранено. Кнопка у СВ скрыта.');
    } catch (e) {
      setMsg('⚠ Не сохранилось: ' + e.message);
    } finally { setSaving(false); }
  }

  if (loading) return <Loader text="Загрузка..." />;
  return (
    <>
      <SectionTitle>Игра «Ловим долю»</SectionTitle>
      <Card style={{ padding: 16 }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.5 }}>
          У СВ над вкладками появляется кнопка «Сыграть в Игру». Она открывает игру в новой вкладке
          и становится активной, только когда прогноз на текущую неделю заполнен — по маршрутам и гипперам.
          Пока не заполнен, кнопка серая и по нажатию показывает, каких дней не хватает.
        </div>
        {loadErr && (
          <div style={{ background: '#FFF4E5', border: '1px solid #F5C27A', borderRadius: 8, padding: '8px 10px', fontSize: 12, marginBottom: 14 }}>
            Таблица настроек не найдена — примените миграцию <b>20261014_app_settings.sql</b>, затем откройте вкладку заново.
            <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>{loadErr}</div>
          </div>
        )}
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, fontWeight: 600, marginBottom: 14, cursor: 'pointer' }}>
          <input id="game-enabled" type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} style={{ width: 18, height: 18 }} />
          Показывать игру СВ
        </label>
        <Input label="Ссылка на игру (https://…)" value={url} onChange={setUrl}
          placeholder="https://storage.yandexcloud.net/lovim-dolyu-open/index.html" />
        {url && !urlOk && <div style={{ fontSize: 12, color: 'var(--red)', marginTop: -8, marginBottom: 12 }}>Ссылка должна начинаться с https://</div>}
        {enabled && !url && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -8, marginBottom: 12 }}>Без ссылки кнопка у СВ не появится.</div>}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <Btn onClick={save} disabled={saving || (url && !urlOk)}>{saving ? 'Сохранение...' : 'Сохранить'}</Btn>
          {urlOk && <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: 'var(--accent)' }}>Проверить ссылку ↗</a>}
        </div>
        {msg && <div style={{ fontSize: 13, marginTop: 12, color: msg.startsWith('⚠') ? 'var(--red)' : 'var(--green)' }}>{msg}</div>}
      </Card>
    </>
  );
}
